import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Switch, Modal } from "antd";
import {
  LockOutlined,
  MailOutlined,
  TeamOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import SingleLogo from "../assets/images/single-logo.svg";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { AuthContext } from "../context/AuthContext";
import Loader from "../components/Loader";
import encryptDecrypt from "../helper/encryptDecrypt";

const SignIn = () => {
  const { setIsLoggedIn, setUserProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();
  const { request } = useRequest();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [form3] = Form.useForm();

  const onFinish = (values) => onSubmit(values);

  const onSubmit = (values) => {
    const { email, password } = values;
    if (!email) return ShowToast("Please enter email to sign in", Severty.ERROR);
    setLoading(true);
    request({
      url: "/admin/auth/login",
      method: "POST",
      data: { email, password },
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          setIsLoggedIn(true);
          if (rememberMe) {
            localStorage.setItem("rememberMe", "true");
            localStorage.setItem("ykmCe2AYEFTHobn", encryptDecrypt.encryptEmail(values.email));
            localStorage.setItem("ouUsippetc8S4Ry", encryptDecrypt.encryptPassword(values.password));
          } else {
            localStorage.removeItem("ykmCe2AYEFTHobn");
            localStorage.removeItem("ouUsippetc8S4Ry");
          }
          localStorage.setItem("admin_token", data.data.token);
          localStorage.setItem("admin_userProfile", JSON.stringify(data.data.user));
          localStorage.setItem("notificationCount", data.data.notificationReadCount);
          setUserProfile(data.data);
          navigate("/dashboard");
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  // errorInfo.values holds the raw form field values (including whatever
  // password was typed) whenever validation fails on submit — logging the
  // whole object put the plaintext password in the console every time.
  const onFinishFailed = () => {};

  const handleForgotPassword = () => setVisible(true);

  const handleResetPassword = (email) => {
    request({
      url: "/admin/auth/forgot-password",
      method: "POST",
      data: { email: email.email },
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setVisible(false);
        setOtpModal(true);
        form1.resetFields();
        setSelected({ email });
      },
      onError: (error) => ShowToast(error.response.data.message, Severty.ERROR),
    });
  };

  const handleVerifyOTP = (values) => {
    request({
      url: "/admin/auth/verify-otp",
      method: "POST",
      data: { email: selected.email.email, otp: values.otp },
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setOtpModal(false);
        setVisible(false);
        setResetModal(true);
        form2.resetFields();
      },
      onError: (error) => ShowToast(error.response.data.message, Severty.ERROR),
    });
  };

  const handleReset = (values) => {
    request({
      url: "/admin/auth/reset-password",
      method: "POST",
      data: { email: selected.email.email, password: values.newPassword },
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setResetModal(false);
      },
      onError: (error) => ShowToast(error.response.data.message, Severty.ERROR),
    });
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("ykmCe2AYEFTHobn");
    const savedPassword = localStorage.getItem("ouUsippetc8S4Ry");
    if (savedEmail && savedPassword) {
      form.setFieldsValue({
        email: encryptDecrypt.decryptEmail(savedEmail),
        password: encryptDecrypt.decryptEmail(savedPassword),
      });
      setRememberMe(true);
    }
  }, []);

  return (
    <>
      {loading && <Loader />}

      <div className="admin-login-shell">
        {/* ── Left: form panel ── */}
        <div className="admin-login-form-panel">
          <div className="admin-login-form-inner">
            <div className="admin-login-logo">
              <img src={SingleLogo} alt="Imergey" />
              <span className="admin-login-badge">Admin</span>
            </div>

            <h1 className="admin-login-heading">Welcome back</h1>
            <p className="admin-login-sub">Sign in to Imergey Admin Panel</p>

            <Form
              form={form}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              className="admin-login-form"
            >
              <Form.Item
                label="Email address"
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Enter a valid email address" },
                  { max: 100, message: "Email cannot exceed 100 characters" },
                ]}
              >
                <Input
                  prefix={<MailOutlined className="admin-input-icon" />}
                  placeholder="you@imergey.com"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                  { max: 100, message: "Password cannot exceed 100 characters" },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ""
                        ? Promise.resolve()
                        : Promise.reject(new Error("Password cannot be empty")),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="admin-input-icon" />}
                  placeholder="••••••••"
                  size="large"
                />
              </Form.Item>

              <div className="admin-login-row">
                <label className="admin-remember-label">
                  <Switch
                    checked={rememberMe}
                    onChange={(checked) => setRememberMe(checked)}
                    size="small"
                  />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  className="admin-forgot-link"
                  onClick={handleForgotPassword}
                >
                  Forgot password?
                </button>
              </div>

              <Form.Item style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  className="admin-login-btn"
                  loading={loading}
                >
                  Sign In
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>

        {/* ── Right: brand panel ── */}
        <div className="admin-login-brand-panel">
          <div className="admin-brand-glow admin-brand-glow--1" />
          <div className="admin-brand-glow admin-brand-glow--2" />

          <div className="admin-brand-content">
            <div className="admin-brand-logo-wrap">
              <img src={SingleLogo} alt="Imergey" className="admin-brand-logo" />
              <span className="admin-brand-name">IMERGEY</span>
            </div>
            <h2 className="admin-brand-headline">Command your<br />brand universe</h2>
            <p className="admin-brand-tagline">
              Manage clients, teams, content, and growth — all from one powerful admin hub.
            </p>

            <div className="admin-brand-stats">
              <div className="admin-stat-card">
                <TeamOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Multi-team</div>
                  <div className="admin-stat-label">Role management</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <BarChartOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Real-time</div>
                  <div className="admin-stat-label">Analytics &amp; reports</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <SafetyCertificateOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Secure</div>
                  <div className="admin-stat-label">Access control</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password modal */}
      <Modal
        open={visible}
        title="Forgot Password"
        okText="Send Reset Link"
        onCancel={() => { setVisible(false); form1.resetFields(); }}
        okButtonProps={{ form: "forget-pasword", htmlType: "submit", loading }}
      >
        <Form id="forget-pasword" form={form1} onFinish={handleResetPassword} layout="vertical">
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { validator: (_, v) => v && v.trim() ? Promise.resolve() : Promise.reject("Email cannot be empty") },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>
        </Form>
      </Modal>

      {/* OTP Modal */}
      <Modal
        open={otpModal}
        title="Verify OTP"
        okText="Verify"
        onCancel={() => { setOtpModal(false); form2.resetFields(); }}
        okButtonProps={{ form: "verify-otp", htmlType: "submit", loading }}
      >
        <Form id="verify-otp" form={form2} onFinish={handleVerifyOTP} layout="vertical">
          <Form.Item label="OTP" name="otp" rules={[{ required: true, message: "Please enter the OTP" }]}>
            <Input placeholder="Enter OTP" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        open={resetModal}
        title="Reset Password"
        okText="Reset"
        onCancel={() => setResetModal(false)}
        okButtonProps={{ form: "reset-password", htmlType: "submit", loading }}
      >
        <Form id="reset-password" form={form3} onFinish={handleReset} layout="vertical">
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[{ required: true, message: "Please enter a new password" }]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SignIn;
